import {NoteGroupIcon} from "crqlar-react-common/dist/components/note/NoteGroupIcon";
import {useNoteGroupIndex} from "crqlar-react-common/dist/utils/hooks/notes/useNoteGroupIndex";
import React, {useMemo, useState} from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Card,
    Chip,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import {useTranslation} from "react-i18next";
import {
    Reservation,
    ReservationStatus
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {
    endOfDay,
    formatDateDMYHM,
    formatDateHM,
    formatDateHMRange,
    startOfDay
} from "crqlar-react-common/dist/utils/date.util";
import {
    FloorplanService
} from "crqlar-react-common/dist/services/floorplan/floorplan.service";
import {
    FloorplanReservationItemDTO
} from "crqlar-react-common/models/floorplan/floorplanReservationItem.dto";
import {getOverlappingReservations} from "../floorPlan/v2/utils/drop-utils";
import {getReservationDisplay} from "../floorPlan/v2/utils/reservation-utils";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {
    useAllChildResources
} from "crqlar-react-common/dist/utils/hooks/resources/useAllRootResources";
import {GeneralSet} from "crqlar-react-common/dist/utils/generalSet";
import {
    useResources
} from "crqlar-react-common/dist/utils/hooks/resources/useResources";
import {
    useProfiles
} from "crqlar-react-common/dist/utils/hooks/profile/useProfiles";
import {
    InternalNode
} from "crqlar-react-common/dist/models/profile/internalNode.model";
import {findProfileById} from "crqlar-react-common/dist/utils/profile.util";
import {useQuery} from "react-query";
import {
    ReservationGroupService
} from "crqlar-react-common/dist/services/reservation/reservationGroup.service";
import Button from "@mui/material/Button";
import {
    useServices
} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {LoadingButton} from "@mui/lab";
import EuroIcon from "@mui/icons-material/Euro";
import DynamicFeedIcon from "@mui/icons-material/DynamicFeed";
import {preparePackageFolio} from "../packages/packageReservation.util";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import ReservationFoliosDialog from "./folio/ReservationFolioDialog";
import SimpleChargeDialog from "../../system/billing/folio/SimpleChargeDialog";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";

interface Props {
    reservation?: Reservation,
    linkedReservations?: Reservation[],
    onOpenReservation?: (reservation: Reservation) => void,
    onUpdateLinkedReservationTable?: (index: number, newResourceIds: string[]) => void
}

const ReservationGroupInfo = ({
                                  reservation,
                                  linkedReservations,
                                  onOpenReservation,
                                  onUpdateLinkedReservationTable
                              }: Props) => {

    const readOnlyUser = useReadOnly()

    const reservationGroupQuery = useQuery(['reservationGroup', reservation?.reservationGroupId], async () => {
        if (!reservation?.reservationGroupId) {
            return {}
        }

        return await ReservationGroupService.getReservationGroupById(reservation?.reservationGroupId)
    })
    const reservationGroup = reservationGroupQuery.data

    const {t} = useTranslation()
    const noteGroupIndex = useNoteGroupIndex()
    const [folioId, setFolioId] = useState<string>()
    const [showChargeDialog, setShowChargeDialog] = useState(false)
    const [showFolios, setShowFolios] = useState(false)

    const organization = useOrganization()

    const resourceIds = new GeneralSet<string>()
    const profileIds = new GeneralSet<string>()
    const serviceIds = new GeneralSet<string>()
    linkedReservations?.forEach(r => {
        resourceIds.addMulti(r.resourceIds || [])
        profileIds.addMulti(r.profileIds || [])
        r.serviceId && serviceIds.add(r.serviceId)
    })

    if (reservationGroup?.serviceId) {
        serviceIds.add(reservationGroup.serviceId)
    }

    const resources = useResources(resourceIds.values())
    const profiles = useProfiles(profileIds.values())
    const services = useServices(serviceIds.values())
    const serviceInfo = services.find(s => s.id === reservationGroup?.serviceId)
    const isPackage = !!serviceInfo?.isPackage

    // --- Conflict detection for multiday reservations ---
    const tableResource = resources.find(r => r.parentId)
    const restaurantId = tableResource?.parentId
    const allTables = useAllChildResources(restaurantId ? [restaurantId] : [])

    const dateRange = useMemo(() => {
        if (!linkedReservations?.length) return null
        let minStart: Date | undefined
        let maxEnd: Date | undefined
        for (const r of linkedReservations) {
            if (r.expectedStartDate) {
                const d = new Date(r.expectedStartDate)
                if (!minStart || d < minStart) minStart = d
            }
            if (r.expectedEndDate) {
                const d = new Date(r.expectedEndDate)
                if (!maxEnd || d > maxEnd) maxEnd = d
            }
        }
        return minStart && maxEnd ? {
            start: startOfDay(minStart),
            end: endOfDay(maxEnd)
        } : null
    }, [linkedReservations])

    const conflictQuery = useQuery(
        ['multidayConflicts', restaurantId, dateRange?.start?.getTime(), dateRange?.end?.getTime()],
        async () => {
            if (!restaurantId || !dateRange) return []
            return FloorplanService.getAssignedReservations({
                restaurantId,
                timespan: {start: dateRange.start, end: dateRange.end},
                status: [ReservationStatus.CONFIRMED, ReservationStatus.ACTIVE, ReservationStatus.COMPLETED, ReservationStatus.PENDING],
                limit: {offset: 0, limit: 1000}
            })
        },
        {enabled: !!restaurantId && !!dateRange}
    )

    const fetchedAssigned = conflictQuery.data || []

    // Sort reservations by date so breakfast + dinner for the same day appear together
    const {sortedLinkedReservations, sortedToOriginalIndex} = useMemo(() => {
        if (!linkedReservations?.length) return {
            sortedLinkedReservations: linkedReservations,
            sortedToOriginalIndex: [] as number[]
        };
        const indexed = linkedReservations.map((r, i) => ({r, originalIndex: i}));
        indexed.sort((a, b) => {
            const aTime = a.r.expectedStartDate ? new Date(a.r.expectedStartDate).getTime() : 0;
            const bTime = b.r.expectedStartDate ? new Date(b.r.expectedStartDate).getTime() : 0;
            return aTime - bTime;
        });
        return {
            sortedLinkedReservations: indexed.map(x => x.r),
            sortedToOriginalIndex: indexed.map(x => x.originalIndex),
        };
    }, [linkedReservations]);

    const conflictMap = useMemo(() => {
        const map = new Map<number, FloorplanReservationItemDTO[]>()
        if (!sortedLinkedReservations?.length || !fetchedAssigned.length) return map

        sortedLinkedReservations.forEach((linked, index) => {
            if (!linked.expectedStartDate || !linked.expectedEndDate) return
            const linkedTableIds = new Set(
                linked.resourceIds?.filter(id =>
                    resources.find(r => r.id === id && r.parentId)
                ) || []
            )
            if (linkedTableIds.size === 0) return

            const newStart = new Date(linked.expectedStartDate).getTime()
            const newEnd = new Date(linked.expectedEndDate).getTime()

            const conflicts = fetchedAssigned.filter(assigned => {
                if (assigned.reservationId === linked.id) return false
                const assignedTableIds = assigned.tableResources?.map(t => t.id) || []
                if (!assignedTableIds.some(id => linkedTableIds.has(id!))) return false
                const existingStart = new Date(assigned.restaurantReservation?.expectedStartDate!).getTime()
                const existingEnd = new Date(assigned.restaurantReservation?.expectedEndDate!).getTime()
                return existingStart < newEnd && newStart < existingEnd
            })

            if (conflicts.length > 0) {
                map.set(index, conflicts)
            }
        })

        return map
    }, [sortedLinkedReservations, fetchedAssigned, resources])

    const conflictCount = conflictMap.size

    const prepareLatestFolio = useQuery(['latestFolio', reservationGroup?.id], async () => {
        if (!reservationGroup?.id || !serviceInfo?.isPackage) return null;

        const packageFolioId = await preparePackageFolio({
            reservationGroup,
            packageService: serviceInfo,
            profileId: reservation?.primaryProfileId || reservation?.profileIds?.[0],
            currencyCode: organization?.currencyCode
        })
        setFolioId(packageFolioId)
        setShowChargeDialog(true)
    }, {
        enabled: false
    })

    const renderSingleReservation = (r: Reservation, index: number) => {
        const canOpen = r.id && r.id !== reservation?.id
        const conflicts = conflictMap.get(index)
        const hasConflict = !!conflicts?.length
        return (
            <Card
                key={r.id || index}
                sx={{
                    p: 1,
                    cursor: canOpen ? 'pointer' : 'default',
                    backgroundColor: r.id && r.id === reservation?.id ? 'primary.light' : 'inherit',
                    ...(hasConflict && {
                        border: '2px solid',
                        borderColor: 'error.main',
                    })
                }}
                onClick={() => canOpen && onOpenReservation?.(r)}
            >
                <Stack
                    gap={0.5}
                >
                    {r.meta?.eatPeriod && (
                        <Typography variant={'body2'} fontStyle={'italic'}
                                    color={'text.secondary'}>
                            {t(`Eat period ${r.meta.eatPeriod}`)}
                        </Typography>
                    )}
                    <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
                        <Typography variant={'subtitle1'}>
                            {services.find(s => s.id === r.serviceId)?.name}
                        </Typography>
                        {isPackage && (
                            r.reservationFromExtraPackageService
                                ? <Chip size={'small'} color={'warning'} variant={'outlined'} label={t('Extra')}/>
                                : <Chip size={'small'} color={'primary'} variant={'outlined'} label={t('Included')}/>
                        )}
                    </Stack>

                    <Stack
                        direction={'row'}
                        gap={2}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                    >
                        <Typography variant={'subtitle1'}>
                            {formatDateDMYHM(r.expectedStartDate)} - {formatDateHM(r.expectedEndDate)}
                        </Typography>
                        <Typography variant={'subtitle1'}>
                            {t(`Reservation status ${r.status}`)}
                        </Typography>
                    </Stack>

                    <Stack
                        direction={'row'}
                        gap={2}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                    >
                        <Typography variant={'subtitle1'}>
                            {
                                r.resourceIds?.map(resourceId => resources.find(r => r.id === resourceId)?.name).join(', ')
                            }
                        </Typography>

                    </Stack>

                    {hasConflict && conflicts.map(c => {
                        const profile = c.profiles?.[0]
                        const name = profile
                            ? [profile.lastName, profile.firstName].filter(Boolean).join(' ')
                            : t('Unknown guest')
                        return (
                            <Typography key={c.reservationId} variant="body2"
                                        color="error.main">
                                {name} {formatDateHMRange(c.restaurantReservation?.expectedStartDate, c.restaurantReservation?.expectedEndDate)}
                            </Typography>
                        )
                    })}

                    {hasConflict && !canOpen && onUpdateLinkedReservationTable && (
                        <Autocomplete
                            disabled={readOnlyUser}
                            size="small"
                            options={allTables.slice().sort((a, b) => {
                                const aConflict = getOverlappingReservations(a.id!, fetchedAssigned, r.expectedStartDate, r.expectedEndDate, r.id).length > 0;
                                const bConflict = getOverlappingReservations(b.id!, fetchedAssigned, r.expectedStartDate, r.expectedEndDate, r.id).length > 0;
                                if (aConflict !== bConflict) return aConflict ? 1 : -1;
                                return (a.name || '').localeCompare(b.name || '', undefined, {numeric: true});
                            })}
                            getOptionLabel={(option: Resource) => option.name || ''}
                            value={allTables.find(t => r.resourceIds?.includes(t.id!)) || null}
                            onChange={(e, value) => {
                                e.stopPropagation()
                                onUpdateLinkedReservationTable(sortedToOriginalIndex[index], value
                                    ? [restaurantId!, value.id!]
                                    : [restaurantId!]
                                )
                            }}
                            renderOption={(props, option) => {
                                const conflicts = getOverlappingReservations(
                                    option.id!,
                                    fetchedAssigned,
                                    r.expectedStartDate,
                                    r.expectedEndDate,
                                    r.id,
                                );
                                const conflict = conflicts[0];
                                const {
                                    guestNames,
                                    roomNames,
                                    timeRange
                                } = getReservationDisplay({
                                    reservation: conflict?.restaurantReservation,
                                    profiles: conflict?.profiles,
                                    accommodationResources: conflict?.accommodationResources,
                                });
                                const detail = [guestNames, roomNames, timeRange].filter(Boolean).join(' · ');
                                return (
                                    <li {...props} key={option.id}>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                            minWidth: 0,
                                            width: '100%'
                                        }}>
                                            <Typography
                                                variant="body2"
                                                fontWeight={500}
                                                color={conflict ? 'error.main' : 'success.main'}
                                                sx={{flex: '0 0 auto'}}
                                            >
                                                {option.name}
                                            </Typography>
                                            {conflict && (
                                                <Typography variant="caption"
                                                            color="error.main"
                                                            noWrap
                                                            sx={{minWidth: 0}}>
                                                    {detail}
                                                    {conflicts.length > 1 ? ` +${conflicts.length - 1}` : ''}
                                                </Typography>
                                            )}
                                        </Box>
                                    </li>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField {...params} label={t('Table')}
                                           size="small"
                                           onClick={(e) => e.stopPropagation()}/>
                            )}
                            sx={{mt: 0.5}}
                        />
                    )}

                </Stack>


            </Card>
        )
    }

    const renderSimpleNote = (note: InternalNode, index: number) => {
        const profile = findProfileById(profiles, note.profileId)
        const nameShort = [profile?.firstName, profile?.lastName?.substring(0, 4)].filter(Boolean).join(' ')

        return (
            <Stack key={index} direction={'row'} alignItems={'flex-start'} gap={0.75}>
                <NoteGroupIcon note={note} index={noteGroupIndex}/>
                <Typography variant={'subtitle1'} sx={{minWidth: 0}}>
                    {
                        note.profileId && <span
                            style={{marginRight: 1}}><strong>{nameShort}: </strong></span>
                    }

                    {note.note}
                </Typography>
            </Stack>
        )
    }

    const renderNotes = () => {
        if (!reservationGroup?.notes?.length) {
            return null
        }

        return (
            <Stack
                sx={{
                    flexDirection: 'column',
                    gap: 1,
                    mt: 2
                }}
            >
                <Typography>
                    {t('Group notes')}
                </Typography>
                {reservationGroup.notes.map(renderSimpleNote)}
            </Stack>
        )
    }


    return (
        <Stack
            sx={{
                flexDirection: 'column',
                width: 300,
                p: 1,
                gap: 1,
                maxHeight: 800,
                overflow: 'auto'
            }}
        >
            {isPackage ? (
                <Stack gap={0.5}>
                    <Stack direction={'row'} alignItems={'center'} gap={1}>
                        <DynamicFeedIcon fontSize={'small'} color={'primary'}/>
                        <Typography variant={'subtitle1'} fontWeight={600}>
                            {t('Package')}: {serviceInfo?.name}
                        </Typography>
                    </Stack>
                    <Typography variant={'body2'} color={'text.secondary'}>
                        {t('These services were booked together as one package')}
                    </Typography>
                </Stack>
            ) : (
                <Typography>
                    {t('Reservations')}
                </Typography>
            )}

            {conflictCount > 0 && (
                <Alert severity="warning">
                    {t('{{count}} days have conflicts', {count: conflictCount})}
                </Alert>
            )}

            <Box sx={{
                flex: 1,
                minHeight: 0,
                maxHeight: 500,
                overflowY: 'auto',
                overflowX: 'hidden',
                background: `
                    linear-gradient(#fff 30%, transparent) center top / 100% 40px,
                    linear-gradient(transparent 70%, #fff) center bottom / 100% 40px,
                    radial-gradient(farthest-side at 50% 0, rgba(0,0,0,.15), transparent) center top / 100% 6px,
                    radial-gradient(farthest-side at 50% 100%, rgba(0,0,0,.15), transparent) center bottom / 100% 6px
                `,
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'local, local, scroll, scroll',
                bgcolor: '#fff',
            }}>
                <Stack sx={{
                    flexDirection: 'column',
                    gap: 1,
                    pb: 1
                }}>
                    {sortedLinkedReservations?.map((r, index) => renderSingleReservation(r, index))}
                </Stack>
            </Box>

            {renderNotes()}

            <Typography mt={3}>
                {t('Reservation group actions')}
            </Typography>

            <Stack direction={'row'} width={'100%'}
                   justifyContent={'space-between'}>
                <Button
                    onClick={() => setShowFolios(true)}
                    variant={'outlined'}
                >
                    <EuroIcon/>
                </Button>

                {/*Product decision - hide*/}
                {/*<LoadingButton*/}
                {/*    variant={'outlined'}*/}
                {/*    onClick={() => {*/}
                {/*        prepareLatestFolio.refetch()*/}
                {/*    }}*/}
                {/*    disabled={!reservationGroup?.id || readOnlyUser}*/}
                {/*    loading={prepareLatestFolio.isLoading}*/}
                {/*>*/}
                {/*    {t('Charge')}*/}
                {/*</LoadingButton>*/}
            </Stack>

            <SimpleChargeDialog
                open={showChargeDialog}
                onClose={() => setShowChargeDialog(false)}
                onSuccess={(folio) => {
                    // setShowChargeDialog(false)
                    // Optionally refresh data or show a success message
                }}
                reservationGroup={reservationGroup}
                service={serviceInfo}
                folioId={folioId}
            />

            <ReservationFoliosDialog
                open={showFolios}
                onClose={() => setShowFolios(false)}
                reservationGroupId={reservationGroup?.id}
                profileId={reservation?.profileIds?.[0]}
            />

        </Stack>
    )

}
export default ReservationGroupInfo;
